import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
    Grid,
    Typography,
    TextField,
    Divider,
    Autocomplete,
    Alert,
    Button
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import Routes from '../routes';
import CommonDialog from './Common/CommonDialog';
import { renderWeight } from '../Utils/weight_utils';
import { Input, WeightInput, LoadingButtonV4, DateInput } from './Common';
import { isMobile, FormatDate } from '../utils';
import { toolTipBlue } from '../color_utils';

const getTransferAmountError = (fromCurrentWeight, transferWeight, toCurrentGasWeightLbs, toMaxCapacity, assetName, toAssetName) => {
    if (transferWeight > fromCurrentWeight) {
        return `The transfer amount entered exceeds the amount available to transfer from ${assetName}. Please review and update`
    }

    if ((transferWeight + toCurrentGasWeightLbs) > toMaxCapacity) {
        return `The transfer amount entered exceeds the amount available to transfer to  ${toAssetName}. Please review and update.`
    }

    return ''
}

const getTransferDateError = (transferDate, acquisitionDate, disbursementDate) => {
    if (!transferDate) return "Date of Transfer is required.";

    if (acquisitionDate) {
        const acquisition = new Date(acquisitionDate);
        if (transferDate < acquisition) {
            return "Date of Transfer cannot be before the Acquisition Date.";
        }
    }
    if (disbursementDate) {
        const disbursement = new Date(disbursementDate);
        if (transferDate > disbursement) {
            return "Date of Transfer cannot be after the Disbursement Date.";
        }
    }

    return "";
};

const AssetDetailsCylinderGasTransfer = ({
    assetId,
    assetName,
    materialTypeName,
    currentGasWeightLBS,
    open,
    onHandleClose,
    onSucessfulTransfer
}) => {
    const [toCylinder, setToCylinder] = useState(null);
    const [transferLbs, setTransferLbs] = useState(0);
    const [transferDate, setTransferDate] = useState(new Date());
    const [technician, setTechnician] = useState('');
    const [notes, setNotes] = useState('');
    const [dropdownData, setDropdownData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);

    const cylinderOptions = dropdownData ? dropdownData.cylinders.map(cylinder => ({
        label: cylinder.name,
        value: cylinder.id
    })) : [];

    const technicianOptions = dropdownData ? dropdownData.technicians.map(tech => ({
        label: tech.fullName,
        value: tech.id
    })) : [];

    const cylinderSeletedDetails = toCylinder && (dropdownData ? dropdownData.cylinders.find(cylinder => cylinder.id === toCylinder) : null)

    const transferAmountError = !cylinderSeletedDetails ? '' : getTransferAmountError(
        currentGasWeightLBS,
        transferLbs,
        cylinderSeletedDetails.currentGasWeightLbs,
        cylinderSeletedDetails.maxGasCapacityLbs,
        assetName,
        cylinderSeletedDetails.name
    );

    const transferDateError = getTransferDateError(transferDate, cylinderSeletedDetails?.acquisitionDate, cylinderSeletedDetails?.disbursementDate);

    useEffect(() => {
        if (!open) {
            setToCylinder(null);
            setTransferLbs(0);
            setTransferDate(new Date());
            setTechnician('');
            setNotes('');
            setIsSaving(false);
            setIsSubmitted(false);
            setCurrentStep(1);
            return;
        }

        const fetchDropdowns = async () => {
            try {
                const response = await fetch(Routes.Asset.GetCylinderTransferDropdowns(assetId));
                const data = await response.json();

                setDropdownData(data);
            } catch (error) {
                console.error('Error fetching dropdowns:', error);
            }
        }

        fetchDropdowns();
    }, [open])

    const handleChangeTransferLbs = (value) => {
        setTransferLbs(value);
    }

    const handleSubmit = async () => {
        setIsSubmitted(true);
        if (!toCylinder || !technician || transferLbs <= 0 || transferAmountError.length > 0 || transferDateError.length > 0) {
            toast.error('Please review the form for missing or incorrect fields', { duration: 6000 });
            return;
        }

        setIsSaving(true);
        const response = await fetch(Routes.Asset.CreateCylinderTransfer(), {
            method: 'POST',
            body: JSON.stringify({
                fromAssetId: assetId,
                toAssetId: toCylinder,
                transferWeightLbs: transferLbs,
                transferDate: FormatDate(transferDate),
                technicianId: technician,
                notes
            }),
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (response.status === 200) {
            await onSucessfulTransfer();
            onHandleClose();
            toast.success('Gas Transfer is complete and changes saved', { duration: 6000 });
        }
        else {
            toast.error('Error saving gas transfer', { duration: 6000 });
        }

        setIsSaving(false);
    }


    const handleCancel = () => {
        toast.error('Gas transfer canceled. No changes were saved', { duration: 6000 });
        onHandleClose();
    }

    return (
        <CommonDialog
            open={open}
            title="Transfer Gas to Another Cylinder"
            fullWidth
            maxWidth="md"
            handleClose={onHandleClose}
            paperSx={isMobile ? {
                margin: 0,
                width: '100vw',
                maxWidth: '100vw',
            } : undefined}
            actionsSx={isMobile ? {
                boxShadow: 'none',
            } : undefined}
            contentSx={isMobile ? {
                padding: '8px 24px'
            } : undefined}
            actionButtons={
                isMobile ? (
                    currentStep === 1 ? (
                        <Grid container direction="column" spacing={3} sx={{ width: '100%' }}>
                            <Grid item xs={12}>
                                <LoadingButtonV4
                                    useLargeButton={false}
                                    fullWidth={true}
                                    buttonText='NEXT'
                                    onClick={() => setCurrentStep(2)}
                                    disabled={!toCylinder || transferLbs <= 0 || transferAmountError.length > 0}
                                />
                            </Grid>
                            <Grid item xs={12} sx={{ textAlign: 'center' }}>
                                <Button
                                    onClick={handleCancel}
                                    fullWidth
                                    sx={{ color: toolTipBlue, textTransform: 'uppercase' }}
                                >
                                    CANCEL
                                </Button>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container direction="column" spacing={3} sx={{ width: '100%' }}>
                            <Grid item xs={12}>
                                <LoadingButtonV4
                                    useLargeButton={false}
                                    fullWidth={true}
                                    buttonText='SUBMIT'
                                    onClick={handleSubmit}
                                    loading={isSaving}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    onClick={() => setCurrentStep(1)}
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<ChevronLeftIcon />}
                                    sx={{
                                        color: toolTipBlue,
                                        borderColor: toolTipBlue,
                                        textTransform: 'uppercase',
                                        height: '42px',
                                        '&:hover': {
                                            borderColor: toolTipBlue,
                                            backgroundColor: 'rgba(0, 121, 143, 0.04)'
                                        }
                                    }}
                                >
                                    PREVIOUS
                                </Button>
                            </Grid>
                        </Grid>
                    )
                ) : (
                    <>
                        <Button sx={{ color: toolTipBlue }} onClick={handleCancel}>
                            Cancel
                        </Button>
                        <LoadingButtonV4
                            useLargeButton={false}
                            fullWidth={false}
                            buttonText='Save'
                            onClick={handleSubmit}
                            loading={isSaving}
                        />
                    </>
                )
            }
        >
            <Grid container spacing={{ xs: 0, sm: 3 }} sx={{ paddingBottom: { xs: '1rem', sm: '2.5rem' } }}>
                {/* Step 1 (mobile only): Transfer From + Transfer To. Desktop shows all content at once. */}
                {(!isMobile || currentStep === 1) && (
                    <>
                        <Grid item xs={12} sm={5}>
                            <Typography variant="body1" sx={{ fontWeight: '700' }} gutterBottom mb={{ xs: 1, sm: 2 }}>
                                Transfer from
                            </Typography>
                            <Input
                                disabled
                                fullWidth
                                alwaysShowHelper
                                value={assetName}
                                variant={"outlined"}
                                label="Cylinder Type"
                                helperText={`Material Type: ${materialTypeName}`}
                                formHelperTextPropsSx={{ textAlign: 'start' }}
                                sx={{ mb: { xs: 1, sm: 2 } }}
                            />

                            <Grid container spacing={2}>
                                <WeightInput
                                    required
                                    headerColor="#656565"
                                    header="Cylinder Current Gas Weight"
                                    variant="outlined"
                                    uselabel
                                    disabled
                                    showConversionArrows={false}
                                    value={currentGasWeightLBS}
                                    onChange={_ => { }}
                                    columnSpan={6}
                                />
                            </Grid>
                        </Grid>

                        <Grid item xs={2} container justifyContent="center" sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: { sm: 'flex-start', lg: 'center' } }}>
                            <ArrowForwardIcon sx={{ fontSize: { sm: '24px', lg: '35px' } }} color="action" />
                        </Grid>
                        <Grid item xs={12} mt={1.5} mb={1.5} sx={{ display: { xs: 'block', sm: 'none' } }}>
                            <Divider sx={{ width: '100%' }} />
                        </Grid>

                        <Grid item xs={12} sm={5}>
                            <Typography variant="body1" sx={{ fontWeight: '700' }} gutterBottom mb={{ xs: 1, sm: 2 }}>
                                Transfer to
                            </Typography>
                            <Autocomplete
                                options={cylinderOptions}
                                getOptionLabel={(option) => option.label}
                                value={
                                    cylinderOptions.find(opt => opt.value === toCylinder) ?? null
                                }
                                onChange={(_, newOption) => {
                                    setToCylinder(newOption?.value ?? null);
                                }}
                                isOptionEqualToValue={(option, value) =>
                                    option.value === value.value
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Cylinder to transfer to"
                                        placeholder="Search or Select"
                                        required
                                        error={isSubmitted && !toCylinder}
                                        helperText={isSubmitted && !toCylinder ? "Cylinder to transfer to is required" : ""}
                                    />
                                )}
                                sx={{ mb: { xs: 1, sm: "38px" } }}
                            />

                            <Grid container spacing={2}>
                                <WeightInput
                                    required
                                    headerColor="#656565"
                                    header="Transfer Amount"
                                    variant="outlined"
                                    showConversionArrows={false}
                                    uselabel
                                    value={transferLbs}
                                    customHelperText={transferAmountError}
                                    lbsError={transferAmountError.length > 0}
                                    onChange={handleChangeTransferLbs}
                                    columnSpan={6}
                                    showHelperTextContainer
                                />
                            </Grid>

                            {
                                (toCylinder && cylinderSeletedDetails) && (
                                    <Alert severity="info" sx={{ mt: 3 }}>
                                        <Typography variant="body2">
                                            <span style={{ fontWeight: '700' }}>Material Type:</span> {cylinderSeletedDetails.materialTypeName}
                                        </Typography>
                                        <Typography variant="body2">
                                            <span style={{ fontWeight: '700' }}>Weight:</span> {renderWeight(cylinderSeletedDetails.currentGasWeightLbs)}
                                        </Typography>
                                        <Typography variant="body2">
                                            <span style={{ fontWeight: '700' }}>Capacity:</span> {renderWeight(cylinderSeletedDetails.maxGasCapacityLbs)}
                                        </Typography>
                                    </Alert>
                                )
                            }
                        </Grid>
                    </>
                )}

                {/* Divider - hide on mobile */}
                <Grid item xs={12} mt={1.5} mb={1.5} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Divider sx={{ width: '100%' }} />
                </Grid>

                {/* Step 2 (mobile only): Date, Technician, Notes. Desktop shows all content at once. */}
                {(!isMobile || currentStep === 2) && (
                    <>
                        <Grid item xs={12} sx={{ display: { xs: 'block', sm: 'none' } }}>
                            <Typography variant="body1" sx={{ fontWeight: '700' }} gutterBottom mb={2}>
                                Details
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <DateInput
                                required
                                fullWidth
                                variant={"outlined"}
                                allowFuture={false}
                                allowPast={true}
                                date={transferDate}
                                onChange={date => setTransferDate(date)}
                                label="Date of Transfer"
                                error={transferDateError.length > 0}
                                customHelperText={transferDateError}
                                sx={{ mb: { xs: 2, sm: 0 } }}
                            />
                        </Grid>
                        <Grid item xs={2} container justifyContent="center" alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                        </Grid>
                        <Grid item xs={12} sm={5} sx={{ mb: { xs: 2, sm: 0 } }}>
                            <Autocomplete
                                options={technicianOptions}
                                getOptionLabel={(option) => option.label}
                                value={
                                    technicianOptions.find(opt => opt.value === technician) ?? null
                                }
                                onChange={(_, newOption) => {
                                    setTechnician(newOption?.value ?? "");
                                }}
                                isOptionEqualToValue={(option, value) =>
                                    option.value === value.value
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Technician"
                                        placeholder="Search or Select"
                                        required
                                        error={isSubmitted && !technician}
                                        helperText={isSubmitted && !technician ? "Technician is required" : ""}
                                    />
                                )}
                                ListboxProps={{
                                    style: {
                                        maxHeight: 200
                                    },
                                    sx: {
                                        "& .MuiAutocomplete-option": {
                                            minHeight: 48
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Notes (Optional)"
                                placeholder="Enter text"
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </CommonDialog>
    );
};

export default AssetDetailsCylinderGasTransfer;
